<script setup>
  import {ref, useId, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentBmpImportDialogRequestedPage from "@components/Dialogs/BmpImportDialog/Tabs/RequestedPage.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "ComponentBmpImportDialogUploadTab",
  });

  defineProps({
    error: {
      type: Object,
      required: true,
    },
    requestedPage: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(["error", "input", "skip"]);

  const fileDragging = ref(false);
  const textInput = ref("");
  const textInputId = useId();

  const processFileUpload = (event) => {
    processFileList(event.target.files);
  };

  const processFileDrop = (event) => {
    fileDragging.value = false;
    processFileList(event.dataTransfer.files);
  };

  const processFileList = async (fileList) => {
    if (fileList.length !== 1) {
      return setError("error", "Bitte laden Sie jeweils nur eine XML-Datei hoch.");
    }

    const item = fileList.item(0);

    if (item.type !== "text/xml") {
      return setError("error", "Bitte laden Sie nur XML-Dateien hoch.");
    }

    try {
      let text = await item.text();

      if (text.includes("�")) {
        text = new TextDecoder("iso-8859-1").decode(await item.arrayBuffer());
      }

      emit("input", text);
      textInput.value = "";
    } catch (error) {
      console.error("Error processing file:", error);

      return setError("error", "Beim Verarbeiten der Datei ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    }
  };

  watch(
    () => textInput.value,
    debounce((newValue) => {
      emit("input", newValue);
      textInput.value = "";
    }, 1000),
  );

  const setError = (type, text) => {
    emit("error", type, text);
  };
</script>

<template>
  <div class="mt-5 flex flex-col gap-3">
    <h3 class="text-lg leading-6 font-medium text-gray-700">BMP-XML hochladen</h3>

    <ComponentAlert
      v-if="error.type && error.text"
      :type="error.type"
      class="mb-0!"
      :test-id="getTestId('xml-upload-tab')"
    >
      {{ error.text }}
    </ComponentAlert>

    <ComponentBmpImportDialogRequestedPage
      v-if="requestedPage"
      :requested-page="requestedPage"
      :test-id="testId + '-requested-page'"
      @skip="$emit('skip')"
    />

    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- drag-and-drop has no keyboard equivalent; the file input inside this label is visually hidden but focusable and reaches the same upload path -->
    <label
      class="flex cursor-pointer flex-col items-center justify-center gap-3 rounded border border-dashed border-gray-700/25 px-6 py-10 hover:border-mcred"
      :class="{'border-mcred!': fileDragging}"
      @drag.prevent="fileDragging = true"
      @dragend.prevent="fileDragging = false"
      @dragenter.prevent="fileDragging = true"
      @dragover.prevent="fileDragging = true"
      @dragleave.prevent="fileDragging = false"
      @drop.prevent="processFileDrop"
    >
      <ComponentIcon class="text-5xl">upload_file</ComponentIcon>

      <span class="text-sm">BMP-XML-Datei per Drag-and-Drop oder Klicken hochladen</span>

      <input
        type="file"
        class="sr-only"
        accept="text/xml"
        :data-test="getTestId('xml-upload-tab-input')"
        @change.prevent="processFileUpload"
      />
    </label>

    <label :for="textInputId">oder BMP-XML-Code manuell einfügen</label>

    <textarea
      :id="textInputId"
      v-model="textInput"
      class="resize-y rounded border border-gray-700/25"
      placeholder="Hier BMP-XML-Code einfügen"
      rows="3"
      :data-test="getTestId('xml-upload-tab-textarea')"
    />
  </div>
</template>
